/**
 * Phase 4.9 — canonical display order for GNSS-system codes.
 *
 * The API returns GNSS arrays alphabetically (``beidou`` < ``galileo``
 * < ``glonass`` < ``gps``), but operators read GNSS by usage frequency
 * — GPS first, GLONASS second, then Galileo, BeiDou, and the regional
 * systems behind those. Same convention as RINEX 3 long-name headers
 * and most published station-capability tables.
 *
 * One canonical table, one sort helper, one application point —
 * ``components/GnssStack.tsx`` — covers every rendering surface (the
 * other call sites pass arrays through to GnssStack as a prop).
 *
 * The ``.join(', ')``-based renders in ``DetailRinexValidation``
 * (missing-systems plate, processed-systems plate) call the helper
 * directly so the comma-separated string respects the same order as
 * the chip stack right next to it.
 */

const GNSS_DISPLAY_ORDER: readonly string[] = [
  'gps',
  'glonass',
  'galileo',
  'beidou',
  'qzss',
  'sbas',
  'irnss',
];

/**
 * Return a new array sorted by canonical display order.
 *
 * Unknown codes (anything not in :data:`GNSS_DISPLAY_ORDER`) sort
 * AFTER every known code, preserving their relative order in the
 * input. Without this guard a future system letter would jump to
 * the very front of every job chip-row — the verbatim-after-known
 * fallback keeps the surface forward-compatible.
 *
 * Pure function over a copy of the input — never mutates the caller's
 * array.
 */
export function sortGnssSystems(systems: readonly string[]): string[] {
  const UNKNOWN_RANK = Number.MAX_SAFE_INTEGER;
  return [...systems].sort((a, b) => {
    const ai = GNSS_DISPLAY_ORDER.indexOf(a.toLowerCase());
    const bi = GNSS_DISPLAY_ORDER.indexOf(b.toLowerCase());
    return (ai === -1 ? UNKNOWN_RANK : ai) - (bi === -1 ? UNKNOWN_RANK : bi);
  });
}
